import { AnimatePresence } from 'framer-motion';
import { FC, memo, useEffect, useState } from 'react';

import Poll from '@/assets/icons/cover-images/neon-poll.png';
import Trivia from '@/assets/icons/cover-images/neon-trivia.png';
import Type from '@/assets/icons/cover-images/neon-type.png';
import You from '@/assets/icons/cover-images/neon-you.png';
import useAlerts from '@/hooks/useAlerts';
import {
  downloadImagesAsZip,
  downloadSelectedImagesAsZip,
  generateImagesInParallel,
  generateSingleImage,
} from '@/utils/coverImages/imageGeneration';
import { getOptimalFontSize as getOptimalFontSizeUtil } from '@/utils/coverImages/textDistribution';
import { replaceQuotes } from '@/utils/helpers';

import { ImageVariation } from '.';
import FloatingBox from '../FloatingBox';
import ImageResults from '../ImageResults';
import PostToArticleButton from '../PostToArticleButton';
import Progress from '../Progress';
import { createWatermarkTagImage } from '../shared';
import VariationsStyles from './VariationsStyles';

interface Props {
  variations?: ImageVariation[];
  images?: string[];
  setImages: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  inArticle?: boolean;
  onReset: () => void;
  isAiGenerated?: boolean;
}

const GeneratedImages: FC<Props> = memo(
  ({ variations, images, setImages, inArticle, onReset, isAiGenerated }) => {
    const alerts = useAlerts();

    const [selectedImages, setSelectedImages] = useState<number[]>([]);
    const [counter, setCounter] = useState(0);
    const [showProgress, setShowProgress] = useState(false);

    const generateImageVariation = async (
      variation: ImageVariation,
    ): Promise<string | undefined> => {
      try {
        return await generateSingleImage(
          () => {
            const imageHolder = document.createElement('div');
            imageHolder.classList.add('image-holder-var26');

            const leftImage = new Image();
            leftImage.src = variation.leftImageSrc;
            leftImage.classList.add('half-image-var26');
            imageHolder.appendChild(leftImage);

            const leftWatermark = createWatermarkTagImage(
              'actual-photo-var26',
              variation.leftWatermarkTag,
              variation.leftWatermarkPosition,
            );
            if (leftWatermark) imageHolder.appendChild(leftWatermark);

            if (variation.divider !== 'none') {
              const middleBreak = document.createElement('div');

              if (variation.divider === 'black') {
                middleBreak.style.backgroundColor = 'black';
              } else if (variation.divider === 'white') {
                middleBreak.style.backgroundColor = 'white';
              }
              middleBreak.classList.add('break-var26');

              imageHolder.appendChild(middleBreak);
            }
            const rightTextHolder = document.createElement('div');
            rightTextHolder.classList.add('right-text-holder-var26');
            if (variation.template === 'poll') {
              rightTextHolder.style.backgroundImage = `url("${Poll}")`;
            } else if (variation.template === 'trivia') {
              rightTextHolder.style.backgroundImage = `url("${Trivia}")`;
            } else if (variation.template === 'type') {
              rightTextHolder.style.backgroundImage = `url("${Type}")`;
            } else if (variation.template === 'you') {
              rightTextHolder.style.backgroundImage = `url("${You}")`;
            }

            if (variation.rightText) {
              const rightText = document.createElement('div');
              rightText.classList.add('right-text-var26');
              const rightTextGlow = document.createElement('div');
              rightTextGlow.classList.add('right-text-glow-var26');

              let text = '';
              const updatedText = replaceQuotes(variation.rightText);
              text = updatedText;

              const algorithmResult = getOptimalFontSizeUtil(text, {
                maxWidth: 400,
                maxHeight: 300,
                fontFamily: 'GothicBold',
                lineHeight: '1.12',
                minFontSize: 45,
                maxFontSize: 120,
              });

              if (variation.brakes) {
                rightText.innerText = text;
                rightTextGlow.innerText = text;
              } else {
                if (
                  typeof algorithmResult === 'object' &&
                  'lines' in algorithmResult
                ) {
                  rightText.innerHTML = algorithmResult.lines.join('<br/>');
                  rightTextGlow.innerHTML = algorithmResult.lines.join('<br/>');

                  if (variation.fontSize) {
                    rightText.style.fontSize = `${variation.fontSize}px`;
                    rightTextGlow.style.fontSize = `${variation.fontSize}px`;
                  } else {
                    rightText.style.fontSize = `${algorithmResult.fontSize}px`;
                    rightTextGlow.style.fontSize = `${algorithmResult.fontSize}px`;
                  }
                }
              }

              rightTextHolder.appendChild(rightText);
              rightTextHolder.appendChild(rightTextGlow);
            }

            imageHolder.appendChild(rightTextHolder);

            return imageHolder;
          },
          {
            holderClassName: 'generated-images-holder-var26',
            pixelRatio: 1,
          },
        );
      } catch (e) {
        alerts.error();
        return undefined;
      }
    };

    const generateAllVariations = async () => {
      if (!variations?.length) return;

      setShowProgress(true);
      setCounter(0);

      try {
        const validImages = await generateImagesInParallel(
          variations,
          generateImageVariation,
          (current) => {
            setCounter(current);
          },
        );

        setImages(validImages);
      } finally {
        setShowProgress(false);
        setCounter(0);
      }
    };

    const handleDownloadZip = async (selected: boolean) => {
      if (!images?.length) return;

      if (selected) {
        await downloadSelectedImagesAsZip(images, selectedImages, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      } else {
        await downloadImagesAsZip(images, {
          filename: 'generated-cover-images.zip',
          imagePrefix: 'image-',
        });
      }
    };

    useEffect(() => {
      generateAllVariations();
      // eslint-disable-next-line
    }, [variations]);

    useEffect(() => {
      if (!images?.length) return;
      setSelectedImages(images?.map((_, index) => index));
    }, [images]);

    return (
      <>
        <VariationsStyles />
        <ImageResults
          images={images}
          selectedImages={selectedImages}
          setSelectedImages={setSelectedImages}
          setImages={setImages}
        />
        <AnimatePresence>
          {!!showProgress && !!variations?.length && (
            <Progress
              key={1}
              progressPercent={(counter / variations.length) * 100}
            />
          )}
          {!inArticle ? (
            <>
              {!!images?.length && (
                <FloatingBox
                  key={2}
                  selectedImages={selectedImages}
                  unselectAll={() => setSelectedImages([])}
                  selectAll={() =>
                    setSelectedImages(images?.map((_, index) => index))
                  }
                  onDownload={handleDownloadZip}
                  images={images}
                />
              )}
            </>
          ) : (
            <>
              {!!images?.length && (
                <PostToArticleButton
                  images={images}
                  selectedImages={selectedImages}
                  onReset={onReset}
                  isAiGenerated={isAiGenerated}
                />
              )}
            </>
          )}
        </AnimatePresence>
      </>
    );
  },
);

export default GeneratedImages;
